// Header.jsx  — single file (Topbar + Header). Paste and import in App.jsx
import React, { useState, useEffect } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { FaTwitter, FaFacebookF, FaInstagram, FaLinkedin } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function Header() {
  const [open, setOpen] = useState(false);

  // ensure body padding is correct for topbar+header heights
  useEffect(() => {
    const setPadding = () => {
      const topbar = document.querySelector(".topbar-fixed");
      const header = document.querySelector(".site-header-fixed");
      const topH = topbar ? topbar.offsetHeight : 40;
      const headH = header ? header.offsetHeight : 72;
      document.documentElement.style.setProperty("--topbar-h", `${topH}px`);
      document.documentElement.style.setProperty("--header-h", `${headH}px`);
      document.body.style.paddingTop = `${topH + headH}px`;
    };
    // run now and on resize
    setPadding();
    window.addEventListener("resize", setPadding);
    return () => {
      window.removeEventListener("resize", setPadding);
      document.body.style.paddingTop = '';
    };
  }, []);

  const links = [
    { name: "Login", to: "/user/login" },
    { name: "Sign-Up", to: "/user/register" },
  ];

  return (
    <>
      {/* Inline critical CSS — highest priority so external styles don't hide topbar/header */}
      <style>{`
        :root { --topbar-h: 40px; --header-h: 82px; }

        /* TOPBAR — fixed at very top */
        .topbar-fixed {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          height: var(--topbar-h) !important;
          display: flex !important;
          align-items: center !important;
          background: #2e2e2e !important;
          color: #fff !important;
          z-index: 99999 !important;
          box-shadow: 0 1px 0 rgba(0,0,0,0.12) !important;
          padding: 20px 0 !important;
        }
        .topbar-inner {
          width: min(1180px,94%);
          margin: 0 auto;
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:12px;
          padding: 0 10px;
        }
        .topbar-left a, .topbar-right a { color: #fff !important; text-decoration:none !important; margin-right:10px; }

        /* HEADER below topbar */
        .site-header-fixed {
          position: fixed !important;
          top: var(--topbar-h) !important;
          left: 0 !important;
          right: 0 !important;
          height: var(--header-h) !important;
          display: flex !important;
          align-items: center !important;
          background: #fff !important;
          z-index: 99998 !important;
          box-shadow: 0 1px 6px rgba(0,0,0,0.06) !important;
          padding: 12px;
          
        }
        .header-inner {
          width: min(1180px,94%);
          margin: 0 auto;
          display:flex;
          align-items:center;
          justify-content:space-between;
          height: 100%;
          padding: 10 10px;
        }

        .logo img { height: 86px; display:block; }

        .nav-desktop { display:flex; }
        .nav-list { list-style:none; margin:0; padding:0; display:flex; gap:24px; align-items:center; }
        .nav-link { color:#222 !important; text-decoration:none !important; font-weight:500 !important; padding:6px 8px; }

        .menu-btn { display:none; background:transparent; border:0; cursor:pointer; padding:6px; }

        /* mobile adjustments */
        @media (max-width:880px) {
          .nav-desktop { display:none !important; }
          .menu-btn { display:block !important; }
          /* show only email & phone in topbar on mobile */
          .topbar-left { display:none !important; }
          .topbar-right { display:flex !important; gap:8px; font-size:13px !important; }
          .logo img { height:48px; }
        }

        /* mobile nav dropdown */
        .nav-mobile {
          display: none;
        }
        .nav-mobile.open {
          display:block;
          position: fixed !important;
          left: 0;
          right: 0;
          top: calc(var(--topbar-h) + var(--header-h));
          z-index: 99997;
          background:#fff;
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          
        }

        /* Force hide common stray nav that used to appear in screenshots */
        body > ul, body > .nav-list { display: none !important; }
      `}</style>

      {/* TOPBAR */}
      <div className="topbar-fixed" role="banner">
        <div className="topbar-inner">
          <div className="topbar-left" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a href="#" aria-label="twitter"><FaTwitter /></a>
            <a href="#" aria-label="facebook"><FaFacebookF /></a>
            <a href="#" aria-label="instagram"><FaInstagram /></a>
            <a href="#" aria-label="linkedin"><FaLinkedin /></a>
          </div>

          <div className="topbar-right" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <a href="mailto:contact@supersteno.com">contact@supersteno.com</a>

            <span>+91 76780 73260</span>
          </div>
        </div>
      </div>

      {/* HEADER */}
      <header className="site-header-fixed" role="navigation">
        <div className="header-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="logo">
              <Link to="/"><img src="https://supersteno.com/assetsa/img/005%20final%20super%20steno%20logo-02%20(2).png" alt="logo" /></Link>
            </div>
          </div>

          <nav className="nav-desktop" aria-label="primary">
            <ul className="nav-list">
              {links.map(l => (
                <li key={l.name}>
                  {l.to.startsWith('#') ? (
                    <a className="nav-link" href={l.to}>{l.name}</a>
                  ) : (
                    <Link className="nav-link" to={l.to}>{l.name}</Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <button className="menu-btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
        </div>
      </header>

      {/* MOBILE NAV */}
      <div className={`nav-mobile ${open ? "open" : ""}`} aria-hidden={!open}>
        <ul style={{ listStyle: "none", margin: 0, padding: 12 }}>
          {links.map(l => (
            <li key={l.name} style={{ padding: "10px 0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
              {l.to.startsWith('#') ? (
                <a href={l.to} onClick={() => setOpen(false)} style={{ textDecoration: "none", color: "#222", fontWeight: 600 }}>{l.name}</a>
              ) : (
                <Link to={l.to} onClick={() => setOpen(false)} style={{ textDecoration: "none", color: "#222", fontWeight: 600 }}>{l.name}</Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
